import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

/**
 * Presentational money-flow diagram for the landing page — a static, demo
 * Sankey (income sources -> net total -> spending groups). No Supabase; safe
 * to render for signed-out visitors. Mirrors the real Analytics "Money Flow".
 * All animation is driven by a single observer on the <svg>, because elements
 * inside <defs>/<clipPath> have no layout box and never trigger whileInView.
 */

const W = 1184;
const H = 470;
const NW = 150;
const SCALE = 0.08;
const TOP = 20;

const CORAL = "hsl(var(--primary))";
const INCOME = "#3BA776";
const BLUE = "#6E8BB8";
const AMBER = "#C9A96A";
const VIOLET = "#8C82B6";

const col0x = 0;
const col1x = (W - NW) / 2;
const col2x = W - NW;

type Node = { label: string; amount: string; value: number; y: number; h: number; color: string };

function stack(items: { label: string; amount: string; value: number; color: string }[], gap: number): Node[] {
  let y = TOP;
  return items.map((it) => {
    const h = it.value * SCALE;
    const node = { ...it, y, h };
    y += h + gap;
    return node;
  });
}

const sources = stack(
  [
    { label: "Salary", amount: "€4,400", value: 4400, color: INCOME },
    { label: "Freelance", amount: "€720", value: 720, color: INCOME },
  ],
  12,
);

const groups = stack(
  [
    // Spending groups add up to €3,880 (the hero's "Spent"); Savings is the €1,240 left over.
    { label: "Housing", amount: "€1,700", value: 1700, color: CORAL },
    { label: "Groceries", amount: "€950", value: 950, color: INCOME },
    { label: "Transport", amount: "€600", value: 600, color: BLUE },
    { label: "Lifestyle", amount: "€630", value: 630, color: AMBER },
    { label: "Savings", amount: "€1,240", value: 1240, color: VIOLET },
  ],
  9,
);

const total: Node = { label: "Net flow", amount: "€5,120", value: 5120, y: TOP, h: 5120 * SCALE, color: CORAL };

function ribbon(x0: number, y0t: number, y0b: number, x1: number, y1t: number, y1b: number) {
  const cx = (x0 + x1) / 2;
  return `M ${x0} ${y0t} C ${cx} ${y0t} ${cx} ${y1t} ${x1} ${y1t} L ${x1} ${y1b} C ${cx} ${y1b} ${cx} ${y0b} ${x0} ${y0b} Z`;
}

let inb = TOP;
const inRibbons = sources.map((s) => {
  const d = ribbon(col0x + NW, s.y, s.y + s.h, col1x, inb, inb + s.h);
  inb += s.h;
  return { d, color: INCOME };
});

let outb = TOP;
const outRibbons = groups.map((g) => {
  const d = ribbon(col1x + NW, outb, outb + g.h, col2x, g.y, g.y + g.h);
  outb += g.h;
  return { d, color: g.color };
});

function NodeRect({ n, x, delay, on }: { n: Node; x: number; delay: number; on: boolean }) {
  return (
    <g>
      <motion.rect
        x={x}
        width={NW}
        rx={8}
        fill={n.color}
        initial={false}
        animate={on ? { height: n.h, y: n.y } : { height: 0, y: n.y + n.h / 2 }}
        transition={{ duration: 0.5, delay, ease: "easeOut" }}
      />
      <text x={x + 12} y={n.y + n.h / 2 - 7} fill="#fff" fontSize="13" fontWeight="700">
        {n.label}
      </text>
      <text x={x + 12} y={n.y + n.h / 2 + 11} fill="#ffffffdd" fontSize="12" fontWeight="500">
        {n.amount}
      </text>
      {n.h >= 90 &&
        [0, 6, 12].map((o) => (
          <circle key={o} cx={x + NW - 12} cy={n.y + n.h / 2 - 6 + o} r="1.6" fill="#ffffff88" />
        ))}
    </g>
  );
}

export function SankeyFlow() {
  const reduce = useReducedMotion() ?? false;
  const ref = useRef<SVGSVGElement>(null);
  const inView = useInView(ref, { once: true, margin: "-12%" });
  const on = reduce || inView;

  return (
    <svg ref={ref} viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Money flow: income sources into net total into spending groups">
      <defs>
        <clipPath id="mf-reveal">
          <motion.rect
            x={0}
            y={0}
            height={H}
            initial={false}
            animate={{ width: on ? W : 0 }}
            transition={{ duration: reduce ? 0 : 1.1, ease: [0.22, 1, 0.36, 1] }}
          />
        </clipPath>
      </defs>

      <g clipPath="url(#mf-reveal)">
        {inRibbons.map((r, i) => (
          <path key={`in-${i}`} d={r.d} fill={r.color} fillOpacity={0.32} />
        ))}
        {outRibbons.map((r, i) => (
          <path key={`out-${i}`} d={r.d} fill={r.color} fillOpacity={0.35} />
        ))}
      </g>

      {sources.map((n, i) => (
        <NodeRect key={n.label} n={n} x={col0x} delay={0.15 + i * 0.08} on={on} />
      ))}
      <NodeRect n={total} x={col1x} delay={0.35} on={on} />
      {groups.map((n, i) => (
        <NodeRect key={n.label} n={n} x={col2x} delay={0.5 + i * 0.07} on={on} />
      ))}
    </svg>
  );
}
